/**
 * Who belongs with whom, read off the guest tags the tracker board already
 * carries — so the seater can keep a family, or a college crowd, at the same
 * table without a new server call.
 *
 * Two of the five tag kinds are social, and they mean different things to a seat:
 *
 *  - `household` — people who live together / arrived as one unit. A STRONG
 *    prefer: they sit at one table when capacity allows.
 *  - `group` — the host's own relationship label ("McCabe Family", "College
 *    friends"). This is the seater's COHORT: a block that sits together.
 *
 * `dietary`, `access` and `logistics` are descriptive and deliberately do not
 * move anyone (an open decision in the PR — a wheelchair tag should probably
 * pull a guest toward an accessible table, but that is a product call).
 *
 * A guest can carry several tags of one kind. The seater needs ONE, so it takes
 * the FIRST in the trip's tag list — the host's own ordering (`position`, then
 * id so equal positions cannot flip between reads). Not alphabetical, not
 * "biggest": the host's list order is the only ordering they ever expressed.
 */
import type { GuestTag, TrackerGuest } from '../services/tracker-api-client';

export interface GroupTag {
  id: string;
  label: string;
  /** The tag's place in the host's list; only ever used to break a tie. */
  order: number;
}

export interface GuestGroups {
  household: GroupTag | null;
  cohort: GroupTag | null;
}

const asGroupTag = (tag: GuestTag): GroupTag => ({ id: tag.id, label: tag.label.trim() || 'Group', order: tag.position });

const byHostOrder = (a: GuestTag, b: GuestTag) =>
  a.position - b.position || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

/** Household and cohort per guest, keyed by invitee id. Empty when the API predates tags. */
export function resolveSeatGroups(
  guests: ReadonlyArray<Pick<TrackerGuest, 'inviteeId' | 'tagIds'>> | null | undefined,
  tags: ReadonlyArray<GuestTag> | null | undefined,
): Map<string, GuestGroups> {
  const out = new Map<string, GuestGroups>();
  if (!guests || !tags || tags.length === 0) return out;
  const byId = new Map(tags.map((tag) => [tag.id, tag]));
  for (const guest of guests) {
    const mine = (guest.tagIds ?? [])
      .map((id) => byId.get(id))
      .filter((tag): tag is GuestTag => Boolean(tag))
      .sort(byHostOrder);
    const household = mine.find((tag) => tag.kind === 'household');
    const cohort = mine.find((tag) => tag.kind === 'group');
    if (household || cohort) {
      out.set(guest.inviteeId, {
        household: household ? asGroupTag(household) : null,
        cohort: cohort ? asGroupTag(cohort) : null,
      });
    }
  }
  return out;
}

/**
 * What the preview chip says: the relationship group, or the household when that
 * is all the guest has; the household is kept separately when both exist.
 */
export function groupLabels(entry: GuestGroups | undefined): { group: string | null; household: string | null } {
  const cohort = entry?.cohort?.label ?? null;
  const household = entry?.household?.label ?? null;
  return { group: cohort ?? household, household: cohort ? household : null };
}
